import { format } from 'date-fns'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, Stack, TableContainer, TextField, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { useGetInternalReportsQuery } from '../../../../api/reports'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { InternalOrganizationPermissions, InternalPermissionType } from '../../../../types/InternalPermission'
import { InternalReport } from '../../../reports/types/InternalReport'
import { useOrganizationAccessChange } from '../../hooks/useOrganizationAccessChange'
import { AccessStatus } from '../AccessStatus/AccessStatus'
import { AccessToggleButton } from '../AccessToggleButton/AccessToggleButton'
import { filterReports, getGrantedIds, getReportTitle, reportVisibilityLabelKeys } from '../helpers/organizationAccessTabHelpers'

type ReportRow = InternalReport & { hasAccess: boolean }

type CustomProps = {
  pendingIds: ReadonlySet<string>
  onToggle: (report: InternalReport, granted: boolean) => void
}

type OrganizationReportsAccessProps = {
  organization: { id: string; name: string }
  permissions: InternalOrganizationPermissions
}

export const OrganizationReportsAccess: FC<OrganizationReportsAccessProps> = ({ organization, permissions }) => {
  const { t } = useTranslation()
  const reportsQuery = useGetInternalReportsQuery()
  const { pendingIds, changeAccess } = useOrganizationAccessChange(organization, InternalPermissionType.Reports)
  const [search, setSearch] = useState('')

  const granted = useMemo(() => getGrantedIds(permissions, InternalPermissionType.Reports), [permissions])
  const rows = useMemo<ReportRow[]>(
    () => filterReports(reportsQuery.data ?? [], search).map((report) => ({ ...report, hasAccess: granted.has(report.id) })),
    [granted, reportsQuery.data, search]
  )
  const grantedCount = rows.filter(({ hasAccess }) => hasAccess).length

  const tableColumns: GRTableColumn<ReportRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:organization_access_cover'),
        accessor: ({ row }) =>
          row.coverUrls[0] ? <Box component="img" src={row.coverUrls[0]} alt="" loading="lazy" sx={{ width: 96, display: 'block', borderRadius: 1 }} /> : null,
        headerCellProps: { sx: { width: 112, minWidth: 112 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_title'),
        accessor: ({ row }) => (
          <Stack>
            <Typography fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
              {getReportTitle(row) || '---'}
            </Typography>
            {!row.published && (
              <Typography variant="caption" color="text.secondary">
                {t('internal-csm:organization_access_unpublished')}
              </Typography>
            )}
          </Stack>
        ),
        sortable: true,
        sortAccessor: ({ row }) => getReportTitle(row).toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 220 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_visibility'),
        accessor: ({ row }) => (reportVisibilityLabelKeys[row.visibility] ? t(reportVisibilityLabelKeys[row.visibility]) : row.visibility),
        sortable: true,
        sortAccessor: ({ row }) => row.visibility,
        headerCellProps: { sx: { minWidth: 100 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_published_at'),
        accessor: ({ row }) => (
          <Typography variant="body2" whiteSpace="nowrap">
            {row.publishedAt ? format(row.publishedAt, 'dd.MM.yyyy HH:mm') : '---'}
          </Typography>
        ),
        sortable: true,
        sortAccessor: ({ row }) => row.publishedAt,
        sortOrder: SortOrder.ASC,
        defaultSortOrder: SortOrder.ASC,
        headerCellProps: { sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-csm:organization_access_access'),
        accessor: ({ row }) => <AccessStatus granted={row.hasAccess} />,
        sortable: true,
        sortAccessor: ({ row }) => (row.hasAccess ? 1 : 0),
        headerCellProps: { sx: { minWidth: 100 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <AccessToggleButton
            granted={row.hasAccess}
            pending={!!customTableProps?.pendingIds.has(row.id)}
            itemName={getReportTitle(row) || row.id}
            onToggle={() => customTableProps?.onToggle(row, !row.hasAccess)}
          />
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { minWidth: 150 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      pendingIds,
      onToggle: (report, grant) => changeAccess({ id: report.id, name: getReportTitle(report) || report.id }, grant),
    }),
    [changeAccess, pendingIds]
  )

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <TextField
          size="small"
          type="search"
          label={t('internal-csm:organization_access_search_reports')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 280 }}
        />
        {reportsQuery.data && (
          <Typography variant="body2" color="text.secondary">
            {t('internal-csm:organization_access_reports_count', { granted: grantedCount, count: rows.length })}
          </Typography>
        )}
      </Stack>
      <QueryStateBoundary
        isLoading={reportsQuery.isLoading}
        isError={reportsQuery.isError}
        errorText={t('internal-csm:organization_access_reports_failed')}
        skeletonHeight={480}
      >
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-csm:organization_access_no_reports')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>
    </Box>
  )
}
